import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  ChangeEvent,
  useMemo,
} from 'react';
import { useHistory } from 'react-router-dom';
import { GlobalContext } from 'providers/GlobalProvider';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import {
  useCreateDatabase,
  useDeleteDatabase,
} from '../../api/bitedb/database/database.hooks';
import { useBulkUploadTariffs } from 'api/bitedb/tariffs/tariffs.hooks';
import RunTariffAnalysisModal from 'components/Analyzers/RunTariffAnalysisModal';
import DeleteConfirmationModal from 'components/Analyzers/DeleteConfirmationModal';

// Material UI & Icons
import { Pagination } from '@mui/material';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import BoltIcon from '@mui/icons-material/Bolt';
import BarChartIcon from '@mui/icons-material/BarChart';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockIcon from '@mui/icons-material/Lock';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';

// API Hooks & Components
import { useGetDatabasesByGroupId } from '../../api/bitedb/database/database.hooks';
import { useGetStatsByGroupId } from 'api/bitedb/stats/stats.hooks';
import { useBulkUploadEvents } from 'hooks/useSocketIO';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RequiredLicense from 'pages/assign/RequiredLicense';
import { LicenseType } from 'pages/assign/LicenseBadge';

const TariffAnalyzer: React.FC = () => {
  const { cur_user, isWhiteLabel } = useContext(GlobalContext);
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();

  // Subscription & Socket States
  const [userHasMonitor, setUserHasMonitor] = useState(true);
  const [processingDatabases, setProcessingDatabases] = useState<Set<string>>(
    new Set(),
  );
  const pendingLinkEntitiesDatabaseIdsRef = useRef<Set<string>>(new Set());

  // UI & View States
  const [activeTab, setActiveTab] = useState('All Runs');
  const [viewMode, setViewMode] = useState<'list' | 'card'>('list');
  const [currentPage, setCurrentPage] = useState(1);
  const [showTariffGuide, setShowTariffGuide] = useState(false);

  // Modal States
  const [showCreateDatabaseModal, setShowCreateDatabaseModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [runToDelete, setRunToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({
    key: 'created_at',
    direction: 'desc',
  });

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key: key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // --- Data Fetching ---
  const {
    data: dbs,
    //isLoading: dbsLoading,
    refetch: refetchDbs,
  } = useGetDatabasesByGroupId(
    userHasMonitor ? cur_user?.selected_team?.id || '' : '',
    '',
  );

  const { data: stats } = useGetStatsByGroupId(
    userHasMonitor ? cur_user?.selected_team?.id ?? '-1' : '-1',
  );

  const runs = Array.isArray(dbs)
    ? dbs.filter((db) => db.type === 'tariffs')
    : [];

  const createDatabaseMutation = useCreateDatabase();
  const bulkUploadTariffsMutation = useBulkUploadTariffs();
  const deleteDatabaseMutation = useDeleteDatabase();

  const processedRuns = useMemo(() => {
    let result = [...runs];

    // 1. Tab Filter
    if (activeTab === 'Completed') {
      result = result.filter((r: any) =>
        ['Complete', 'Completed', 'complete'].includes(r.status),
      );
    } else if (activeTab === 'Running') {
      result = result.filter((r: any) =>
        ['Running', 'Pending', 'in-progress'].includes(r.status),
      );
    }

    // 2. Search Filter (By Name or Database Name)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((r: any) =>
        (r.name || r.database_name || '').toLowerCase().includes(query),
      );
    }

    // 3. Sorting
    result.sort((a: any, b: any) => {
      let aValue: any = a[sortConfig.key];
      let bValue: any = b[sortConfig.key];

      // Robust checks for Tariff-specific data structures
      if (sortConfig.key === 'findings_count') {
        aValue = Number(
          a.findings_count ??
            a.issues_found ??
            a.hit_count ??
            a.nr_tariffs ??
            0,
        );
        bValue = Number(
          b.findings_count ??
            b.issues_found ??
            b.hit_count ??
            b.nr_tariffs ??
            0,
        );
      } else if (sortConfig.key === 'name') {
        aValue = (a.name || a.database_name || '').toLowerCase();
        bValue = (b.name || b.database_name || '').toLowerCase();
      } else if (sortConfig.key === 'created_at') {
        aValue = new Date(a.created_at || a.screening_timestamp || 0).getTime();
        bValue = new Date(b.created_at || b.screening_timestamp || 0).getTime();
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [runs, activeTab, searchQuery, sortConfig]);

  const handleRunAnalysis = async (
    name: string,
    file: File,
    spendMode: 'trial' | 'active',
  ) => {
    if (isInteractionDisabled) {
      return;
    }

    const teamId = cur_user?.selected_team?.id;
    if (!teamId) throw new Error('No active workspace/team selected.');

    // 1. Create the Database
    const database = {
      name: name,
      description: '',
      type: 'tariffs',
      group_id: teamId,
      created_by: cur_user?.basics?.id,
      screening_frequency: 'Once', // Defaults required by backend
    };

    const response = await createDatabaseMutation.mutateAsync(database);
    if (!response?.success || !response?.result?.data) {
      throw new Error('Failed to create database');
    }

    const createdDb = response.result.data;
    handleDatabaseCreated(createdDb.id.toString());

    // 2. Start Bulk Upload
    return new Promise<void>((resolve, reject) => {
      bulkUploadTariffsMutation.mutate(
        {
          databaseId: createdDb.id,
          file: file,
          createdBy: cur_user.basics?.id || '',
          teamId: teamId,
          dateStrategy: 'original', // Defaults since we removed frequency screen
          spendMode,
        },
        {
          onSuccess: () => resolve(),
          onError: (err) => reject(err),
        },
      );
    });
  };

  const confirmDeleteRun = (e: React.MouseEvent, run: any) => {
    if (isInteractionDisabled) {
      return;
    }
    e.stopPropagation();
    setRunToDelete({ id: run.id, name: run.name });
    setDeleteModalOpen(true);
  };

  const executeDeleteRun = async () => {
    if (isInteractionDisabled) {
      return;
    }
    if (!runToDelete) return;
    setIsDeleting(true);
    try {
      await deleteDatabaseMutation.mutateAsync({
        id: runToDelete.id,
        groupId: cur_user?.selected_team?.id,
      });

      setDeleteModalOpen(false);
      setRunToDelete(null);
      refetchDbs();
      enqueueSnackbar('Analysis deleted successfully.', { variant: 'success' });
    } catch (error) {
      console.error('Failed to delete run:', error);
      enqueueSnackbar('Failed to delete the run. Please try again.', {
        variant: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // --- Socket Events ---
  useBulkUploadEvents(undefined, {
    onComplete: (result) => {
      if (isInteractionDisabled) {
        return;
      }
      if (result.databaseId) {
        const completedDatabaseId = result.databaseId.toString();
        setProcessingDatabases((prev) => {
          const updated = new Set(prev);
          updated.delete(completedDatabaseId);
          return updated;
        });
        if (
          pendingLinkEntitiesDatabaseIdsRef.current.has(completedDatabaseId)
        ) {
          pendingLinkEntitiesDatabaseIdsRef.current.delete(completedDatabaseId);
        }
        refetchDbs();
        enqueueSnackbar(`Tariffs uploaded successfully!`, {
          variant: 'success',
        });
      }
    },
    onFailed: (result) => {
      if (isInteractionDisabled) {
        return;
      }
      if (result.databaseId) {
        const failedDatabaseId = result.databaseId.toString();
        setProcessingDatabases((prev) => {
          const updated = new Set(prev);
          updated.delete(failedDatabaseId);
          return updated;
        });
        enqueueSnackbar(result.error || 'Upload failed.', { variant: 'error' });
      }
    },
  });

  const handleDatabaseCreated = (databaseId: string) =>
    setProcessingDatabases((prev) => new Set(prev).add(databaseId));

  useEffect(() => {
    checkForMonitor();
  }, []);

  const requiredTiers = ['pro', 'business', 'enterprise', 'tariff-only'];

  const checkForMonitor = () => {
    if (
      !isWhiteLabel &&
      !requiredTiers.includes(cur_user.license) &&
      cur_user?.bite_super_admin !== true
    ) {
      setUserHasMonitor(false);
      return;
    }
    setUserHasMonitor(true);
  };

  const isInteractionDisabled = !userHasMonitor;
  const normalizedLicense = (
    ['none', 'standard', 'pro', 'business', 'enterprise'].includes(
      cur_user?.license,
    )
      ? cur_user.license
      : 'none'
  ) as LicenseType;

  // Filter helper: a run is "Running" when the server says in-progress
  // OR when we just kicked it off this session (optimistic flag).
  const isRunActive = (run: any) => {
    if (!run?.id) return false;
    const status = (run as any).last_screening_status;
    return (
      status === 'in-progress' || processingDatabases.has(run.id.toString())
    );
  };

  const filteredRuns =
    activeTab === 'All Runs'
      ? runs
      : activeTab === 'Completed'
      ? runs.filter((run) => !isRunActive(run))
      : runs.filter(isRunActive);

  const itemsPerPage = viewMode === 'list' ? 10 : 9;
  const totalPages = Math.ceil(filteredRuns.length / itemsPerPage) || 1;
  const paginatedRuns = processedRuns.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  useEffect(() => setCurrentPage(1), [activeTab, viewMode]);

  const handlePageChange = (e: ChangeEvent<unknown>, new_page: number) =>
    setCurrentPage(new_page);

  // A run is "Running" when either:
  //   * the server-side ScreeningResult.status is 'in-progress' (the
  //     authoritative chunk-processor signal exposed by
  //     getDatabasesByGroupId), or
  //   * we just kicked it off in this session and the server hasn't had
  //     time to publish the in-progress status yet (immediate optimistic
  //     feedback, cleared once the socket / next refetch confirms state).
  // A run is "Failed" when the server screening status is 'failed'.
  const getStatusBadge = (runId: string) => {
    const idStr = runId.toString();
    const run = runs.find((r) => r.id?.toString() === idStr) as any;
    const screeningStatus = run?.last_screening_status as
      | string
      | null
      | undefined;
    const isRunning =
      screeningStatus === 'in-progress' || processingDatabases.has(idStr);
    const didFail = screeningStatus === 'failed';
    if (isRunning) {
      return <span className='run-badge running'>Running</span>;
    }
    if (didFail) {
      return (
        <span
          className='run-badge failed'
          style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}
        >
          Failed
        </span>
      );
    }
    return <span className='run-badge completed'>Completed</span>;
  };

  // --- KPI Values ---
  const totalRuns = runs.length;
  const totalHTSCodes =
    stats?.total_tariffs ??
    runs.reduce((acc, db) => acc + (db?.nr_tariffs ?? 0), 0);
  const needsReviewCount = stats?.total_not_reviewed_tariffs || 0;
  const inProgressCount = runs.reduce(
    (acc, run) => acc + (isRunActive(run) ? 1 : 0),
    0,
  );

  // Auto-poll the database list while ANY tariff run is in flight so the
  // Running → Completed transition shows up without a manual refresh.
  // Also self-cleans processingDatabases entries the moment the server
  // says they're done — the local Set was the only thing keeping the
  // "Running" badge sticky after socket-complete fires too early (which
  // happens because that socket emits on DB-insert completion, not on
  // chunk-processor completion).
  useEffect(() => {
    if (inProgressCount === 0) return;
    const interval = setInterval(() => {
      refetchDbs();
    }, 4000);
    return () => clearInterval(interval);
  }, [inProgressCount, refetchDbs]);

  useEffect(() => {
    if (processingDatabases.size === 0) return;
    setProcessingDatabases((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of prev) {
        const run = runs.find((r) => r.id?.toString() === id) as any;
        const status = run?.last_screening_status;
        if (status === 'complete' || status === 'failed') {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [runs, processingDatabases]);

  return (
    <>
      <div className='analyzers-dashboard-root'>
        {/* Header Section */}
        <div className='detail-header-flex'>
          <div>
            <h1 className='analyzers-page-title'>Tariff Analyzer</h1>
            <p className='analyzers-page-subtitle'>
              Upload, calculate and store duty and tariffs data related to your
              products.
            </p>
          </div>
          <div className='detail-header-actions'>
            <button
              className='btn-primary'
              onClick={() => {
                if (!isInteractionDisabled) {
                  setShowCreateDatabaseModal(true);
                }
              }}
              disabled={isInteractionDisabled}
              style={{
                cursor: isInteractionDisabled ? 'not-allowed' : 'pointer',
                opacity: isInteractionDisabled ? 0.7 : 1,
              }}
            >
              {isInteractionDisabled && (
                <LockIcon style={{ fontSize: 16, marginRight: 6 }} />
              )}
              <BoltIcon style={{ fontSize: 18 }} /> Run New Analysis
            </button>
          </div>
        </div>

        {!isWhiteLabel && !userHasMonitor && (
          <RequiredLicense
            userLicense={normalizedLicense}
            minimumLicense='pro'
          />
        )}

        {/* KPI Row */}
        <div className='detail-kpi-grid'>
          <div className='detail-kpi-card'>
            <div className='kpi-card-header'>
              <BarChartIcon style={{ fontSize: 16, color: '#4895ef' }} />
              <span className='kpi-card-label'>Total Runs</span>
            </div>
            <div className='kpi-card-value text-blue'>
              {totalRuns.toLocaleString()}
            </div>
          </div>

          <div className='detail-kpi-card'>
            <div className='kpi-card-header'>
              <TextSnippetIcon style={{ fontSize: 16, color: '#20c997' }} />
              <span className='kpi-card-label'>All HTS Codes</span>
            </div>
            <div className='kpi-card-value text-teal'>
              {totalHTSCodes.toLocaleString()}
            </div>
          </div>

          <div className='detail-kpi-card'>
            <div className='kpi-card-header'>
              <WarningAmberIcon style={{ fontSize: 16, color: '#f79009' }} />
              <span className='kpi-card-label'>Needs Review</span>
            </div>
            <div className='kpi-card-value text-orange'>
              {needsReviewCount.toLocaleString()}
            </div>
          </div>

          <div className='detail-kpi-card'>
            <div className='kpi-card-header'>
              <AccessTimeIcon style={{ fontSize: 16, color: '#4895ef' }} />
              <span className='kpi-card-label'>In Progress</span>
            </div>
            <div className='kpi-card-value text-blue'>
              {inProgressCount.toLocaleString()}
            </div>
          </div>
        </div>

        {/* "New to Tariff Analyzer" Dropdown */}
        <div
          style={{ width: '100%', padding: '0 24px', boxSizing: 'border-box' }}
        >
          <button
            type='button'
            onClick={() => {
              if (!isInteractionDisabled) {
                setShowTariffGuide((prev) => !prev);
              }
            }}
            disabled={isInteractionDisabled}
            style={{
              width: '100%',
              background: 'var(--Background-200, var(--bg-colors))',
              border: '1px solid var(--base-border)',
              borderRadius: '8px',
              minHeight: '44px',
              color: 'var(--text-primary)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 14px',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '16px',
                fontWeight: 500,
              }}
            >
              <HelpOutlineOutlinedIcon
                sx={{ fontSize: 18, color: 'var(--Primary-400, #4895ef)' }}
              />
              New to the Tariff Analyzer? Learn how statuses work and tips for
              clean uploads
            </span>
            {showTariffGuide ? (
              <ExpandLessOutlinedIcon
                sx={{ fontSize: 20, color: 'var(--card-text)' }}
              />
            ) : (
              <ExpandMoreOutlinedIcon
                sx={{ fontSize: 20, color: 'var(--card-text)' }}
              />
            )}
          </button>

          {showTariffGuide && (
            <div
              style={{
                display: 'flex',
                gap: '16px',
                marginTop: '16px',
                flexWrap: 'wrap',
              }}
            >
              <div
                style={{
                  flex: '1 1 420px',
                  background: 'var(--Background-200, var(--bg-colors))',
                  border: '1px solid var(--base-border)',
                  borderRadius: '10px',
                  padding: '18px',
                }}
              >
                <div
                  className='api-container-left-h1'
                  style={{
                    fontSize: '18px',
                    marginBottom: '6px',
                    paddingTop: '0px',
                  }}
                >
                  Record Status Guide
                </div>
                <div
                  className='card-db-meta-span'
                  style={{
                    display: 'block',
                    marginBottom: '16px',
                    paddingTop: '0px',
                  }}
                >
                  Each record is assigned a status based on data completeness
                  and tariff compliance checks.
                </div>
                <div
                  style={{
                    border: '1px solid #1B7852',
                    borderRadius: '8px',
                    background: 'rgba(22, 163, 74, 0.08)',
                    padding: '12px',
                    marginBottom: '12px',
                  }}
                >
                  <div
                    className='card-db-title'
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <CheckCircleOutlineOutlinedIcon
                      sx={{
                        color: '#22C55E',
                        fontSize: 18,
                        marginRight: '6px',
                      }}
                    />{' '}
                    Green — Complete
                  </div>
                  <div
                    className='card-db-meta-span'
                    style={{ marginTop: '6px', lineHeight: '1.5' }}
                  >
                    All required checks pass. No critical blockers or warning
                    flags were detected.
                  </div>
                </div>
                <div
                  style={{
                    border: '1px solid #C68A18',
                    borderRadius: '8px',
                    background: 'rgba(245, 158, 11, 0.08)',
                    padding: '12px',
                    marginBottom: '12px',
                  }}
                >
                  <div
                    className='card-db-title'
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <WarningAmberOutlinedIcon
                      sx={{
                        color: '#F59E0B',
                        fontSize: 18,
                        marginRight: '6px',
                      }}
                    />{' '}
                    Yellow — Review Needed
                  </div>
                  <div
                    className='card-db-meta-span'
                    style={{ marginTop: '6px', lineHeight: '1.5' }}
                  >
                    Exemption codes or FTAs may be available, or default values
                    are being used.
                  </div>
                </div>
                <div
                  style={{
                    border: '1px solid #AD2747',
                    borderRadius: '8px',
                    background: 'rgba(239, 68, 68, 0.08)',
                    padding: '12px',
                  }}
                >
                  <div
                    className='card-db-title'
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <HighlightOffOutlinedIcon
                      sx={{
                        color: '#EF4444',
                        fontSize: 18,
                        marginRight: '6px',
                      }}
                    />{' '}
                    Red — Critical / Barred
                  </div>
                  <div
                    className='card-db-meta-span'
                    style={{ marginTop: '6px', lineHeight: '1.5' }}
                  >
                    Critical errors detected like invalid HTS codes or missing
                    ISO country codes.
                  </div>
                </div>
              </div>

              <div
                style={{
                  flex: '1 1 420px',
                  background: 'var(--Background-200, var(--bg-colors))',
                  border: '1px solid var(--base-border)',
                  borderRadius: '10px',
                  padding: '18px',
                }}
              >
                <div
                  className='api-container-left-h1'
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '18px',
                    marginBottom: '6px',
                  }}
                >
                  <TipsAndUpdatesOutlinedIcon
                    sx={{ fontSize: 18, color: 'var(--Primary-400, #4895ef)' }}
                  />{' '}
                  Upload Tips
                </div>
                <div
                  className='card-db-meta-span'
                  style={{ lineHeight: '1.5', marginBottom: '12px' }}
                >
                  Follow these guidelines to reduce errors during bulk uploads.
                </div>
                <ol
                  className='card-db-meta-span'
                  style={{ margin: '0 0 0 20px', lineHeight: '1.6' }}
                >
                  <li>Use full HTS codes — include all digits.</li>
                  <li>
                    Use ISO country codes — two-letter ISO 3166-1 alpha-2.
                  </li>
                  <li>
                    Include product value — each row should include a numeric
                    value.
                  </li>
                  <li>Verify origin details for high-duty aluminum.</li>
                  <li>Keep one product per row.</li>
                </ol>
              </div>
            </div>
          )}
        </div>

        {/* Analysis Runs Section */}
        <div className='analyzers-section'>
          <div className='analyzers-section-header'>
            <h2 className='section-title'>Analysis Runs</h2>
            <p className='section-subtitle'>
              Click a run to view its full tariff details.
            </p>
          </div>

          <div
            className='runs-toolbar'
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '16px',
            }}
          >
            <div className='runs-tabs'>
              {/* NEW: Tariff Search Bar */}
              <div className='data-search-wrapper'>
                <SearchIcon
                  className='data-search-icon'
                  style={{ fontSize: 18 }}
                />
                <input
                  type='text'
                  className='data-search-input'
                  placeholder='Search by Name'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              {['All Runs', 'Completed', 'Running'].map((tab) => (
                <button
                  key={tab}
                  className={`runs-tab ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => {
                    if (!isInteractionDisabled) {
                      setActiveTab(tab);
                    }
                  }}
                  disabled={isInteractionDisabled}
                  style={{
                    cursor: isInteractionDisabled ? 'not-allowed' : 'pointer',
                    opacity: isInteractionDisabled ? 0.65 : 1,
                  }}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className='view-toggle-group'>
              <button
                className={`view-toggle-btn ${
                  viewMode === 'list' ? 'active' : ''
                }`}
                onClick={() => {
                  if (!isInteractionDisabled) {
                    setViewMode('list');
                  }
                }}
                disabled={isInteractionDisabled}
                title='List View'
                style={{
                  cursor: isInteractionDisabled ? 'not-allowed' : 'pointer',
                  opacity: isInteractionDisabled ? 0.65 : 1,
                }}
              >
                <ViewListIcon style={{ fontSize: 20 }} />
              </button>
              <button
                className={`view-toggle-btn ${
                  viewMode === 'card' ? 'active' : ''
                }`}
                onClick={() => {
                  if (!isInteractionDisabled) {
                    setViewMode('card');
                  }
                }}
                disabled={isInteractionDisabled}
                title='Card View'
                style={{
                  cursor: isInteractionDisabled ? 'not-allowed' : 'pointer',
                  opacity: isInteractionDisabled ? 0.65 : 1,
                }}
              >
                <GridViewIcon style={{ fontSize: 20 }} />
              </button>
            </div>
          </div>

          <div
            className='runs-content-container'
            style={
              viewMode === 'card'
                ? { background: 'transparent', border: 'none' }
                : {}
            }
          >
            {/* List View */}
            {viewMode === 'list' && (
              <table className='runs-table'>
                <thead>
                  <tr>
                    <th
                      onClick={() => handleSort('name')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        Run Name
                        {sortConfig.key === 'name' &&
                          (sortConfig.direction === 'asc' ? (
                            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          ))}
                      </div>
                    </th>
                    <th>Status</th>
                    <th
                      onClick={() => handleSort('hts_codes_count')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        HTS Codes
                        {sortConfig.key === 'hts_codes_count' &&
                          (sortConfig.direction === 'asc' ? (
                            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          ))}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('created_at')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        Date
                        {sortConfig.key === 'created_at' &&
                          (sortConfig.direction === 'asc' ? (
                            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          ))}
                      </div>
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRuns.map((run) => (
                    <tr
                      key={run.id}
                      onClick={() => {
                        if (!isInteractionDisabled) {
                          history.push(`/tariff-analyzers/${run.id}`);
                        }
                      }}
                      style={{
                        cursor: isInteractionDisabled
                          ? 'not-allowed'
                          : 'pointer',
                      }}
                      className='hoverable-row'
                    >
                      <td className='run-name'>{run.name}</td>
                      <td>{getStatusBadge(run.id || '')}</td>
                      <td>{run.nr_tariffs || 0}</td>
                      <td>
                        {run?.created_at
                          ? moment(run.created_at).format('MM/DD/YYYY')
                          : '--'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                          }}
                        >
                          <button
                            onClick={(e) => confirmDeleteRun(e, run)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'var(--red-color, #ef4444)',
                              cursor: isInteractionDisabled
                                ? 'not-allowed'
                                : 'pointer',
                              padding: 0,
                              opacity: isInteractionDisabled ? 0.5 : 1,
                            }}
                            title='Delete Analysis'
                            disabled={isInteractionDisabled}
                          >
                            <DeleteOutlineIcon style={{ fontSize: 24 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {paginatedRuns.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        style={{ textAlign: 'center', padding: '24px' }}
                      >
                        No runs found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {/* Card View */}
            {viewMode === 'card' && (
              <div className='card-grid-three'>
                {paginatedRuns.map((run) => (
                  <div
                    key={run.id}
                    className='card-db hoverable-card'
                    onClick={() => {
                      if (!isInteractionDisabled) {
                        history.push(`/tariff-analyzers/${run.id}`);
                      }
                    }}
                    style={{
                      minHeight: '160px',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: isInteractionDisabled ? 'not-allowed' : 'pointer',
                      opacity: isInteractionDisabled ? 0.8 : 1,
                    }}
                  >
                    <div
                      className='card-db-header'
                      style={{
                        marginBottom: '16px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                      }}
                    >
                      <div>
                        <div
                          className='card-db-title'
                          style={{
                            fontSize: '16px',
                            lineHeight: '1.4',
                            marginBottom: '8px',
                          }}
                        >
                          {run.name}
                        </div>
                        {getStatusBadge(run.id || '')}
                      </div>
                      <button
                        onClick={(e) => confirmDeleteRun(e, run)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--red-color, #ef4444)',
                          cursor: isInteractionDisabled
                            ? 'not-allowed'
                            : 'pointer',
                          padding: '4px',
                          opacity: isInteractionDisabled ? 0.5 : 1,
                        }}
                        title='Delete Analysis'
                        disabled={isInteractionDisabled}
                      >
                        <DeleteOutlineIcon style={{ fontSize: 24 }} />
                      </button>
                    </div>
                    <div
                      className='card-db-meta-col'
                      style={{
                        alignItems: 'flex-start',
                        gap: '8px',
                        marginBottom: '16px',
                      }}
                    >
                      <span
                        className='card-db-meta-span'
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: '100%',
                        }}
                      >
                        <span>HTS Codes:</span>
                        <strong style={{ color: 'var(--text-primary-color)' }}>
                          {run.nr_tariffs || 0}
                        </strong>
                      </span>
                      <span
                        className='card-db-meta-span'
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: '100%',
                        }}
                      >
                        <span>Date:</span>
                        <strong style={{ color: 'var(--text-primary-color)' }}>
                          {run?.created_at
                            ? moment(run.created_at).format('MM/DD/YYYY')
                            : '--'}
                        </strong>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination Footer */}
            {filteredRuns.length > 0 && (
              <div
                className='hb-pagination'
                style={
                  viewMode === 'list'
                    ? {
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '16px',
                        flexWrap: 'wrap',
                        padding: '16px 24px',
                        borderTop: '1px solid var(--es-soft-border)',
                        background: 'var(--Background-200, var(--bg-colors))',
                      }
                    : {
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '16px',
                        marginTop: '24px',
                      }
                }
              >
                {viewMode === 'list' && (
                  <span
                    className='pagination-text'
                    style={{ color: 'var(--text-secondary)', fontSize: '14px' }}
                  >
                    Page {currentPage} of {totalPages}
                  </span>
                )}
                <Pagination
                  onChange={handlePageChange}
                  size='large'
                  count={totalPages}
                  shape='rounded'
                  variant='text'
                  page={currentPage}
                  disabled={isInteractionDisabled}
                />
              </div>
            )}
          </div>
        </div>

        <RunTariffAnalysisModal
          open={showCreateDatabaseModal}
          handleClose={() => setShowCreateDatabaseModal(false)}
          onAnalyze={handleRunAnalysis}
          cur_user={cur_user}
          isWhiteLabel={isWhiteLabel}
        />
        <DeleteConfirmationModal
          open={deleteModalOpen}
          handleClose={() => {
            setDeleteModalOpen(false);
            setRunToDelete(null);
          }}
          onDelete={executeDeleteRun}
          isDeleting={isDeleting}
          runName={runToDelete?.name}
        />
      </div>
    </>
  );
};

export default TariffAnalyzer;
